import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Render as a landmark-less section/article when meaningful. */
  as?: 'div' | 'section' | 'article';
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card({ as: Tag = 'div', className, ...props }, ref) {
  return <Tag ref={ref} className={cn('min-w-0 rounded-card border border-border-subtle bg-surface-default shadow-card', className)} {...props} />;
});

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Right-aligned actions next to the title block. */
  actions?: React.ReactNode;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader({ className, actions, children, ...props }, ref) {
  return (
    <div ref={ref} className={cn('flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-2', className)} {...props}>
      <div className="grid min-w-0 flex-1 gap-0.5">{children}</div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
});

export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  level?: 2 | 3 | 4;
}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle({ level = 3, className, ...props }, ref) {
  const H = `h${level}` as 'h2' | 'h3' | 'h4';
  return <H ref={ref} className={cn('text-body-lg font-semibold text-text-primary', className)} {...props} />;
});

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(function CardDescription(
  { className, ...props },
  ref,
) {
  return <p ref={ref} className={cn('text-body text-text-secondary', className)} {...props} />;
});

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function CardContent({ className, ...props }, ref) {
  return <div ref={ref} className={cn('px-5 py-3', className)} {...props} />;
});

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function CardFooter({ className, ...props }, ref) {
  return <div ref={ref} className={cn('flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle px-5 py-3', className)} {...props} />;
});
